import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Alert,
  alpha,
  Box,
  Button,
  Checkbox,
  SelectChangeEvent,
  TablePagination,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { GridRowsProp } from "@mui/x-data-grid";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { convertPenniesToCurrency } from "../../../AccountMenu";
import {
  useOutletContext,
  useRevalidator,
  useSearchParams,
} from "react-router-dom";
import { FinesSortControl } from "../HostSystemItemSortControl";
import { HostSystemItemSortType } from "@tlc/ls2pac/dto/HostSystemItemSortType";
import { useLanguage } from "../../../Language";
import PrintIcon from "@mui/icons-material/Print";
import { useIsMobile } from "@tlc/ui/useIsMobile";
import PrintDialog from "../PrintDialog";
import { useLocationConfiguration } from "@tlc/ls2pac/components/LocationConfiguration";
import {
  FinesPaymentReceiptDialog,
  FinesPaymentResponse,
} from "./FinesPaymentReceiptDialog";
import axios from "axios";
import { FinesPaymentDialog } from "./FinesPaymentDialog";
import { FinesGridSkeleton, FinesToolbarSkeleton } from "./FinesSkeltons";
import { useDialog } from "@tlc/ui/dialog/useDialog";
import { FinesDesktopGrid } from "./FinesDesktopGrid";
import { FinesMobileCards } from "./FinesMobileCards";
import { useAccount } from "@tlc/ls2pac/components/Account";
import { useErrorDialog } from "@tlc/ui/dialog/error/ErrorDialogContext";
import { useSimpleErrorDialog } from "../SimpleErrorDialog";

export interface FinesGridRowModel
  extends Pick<
    HostSystemItemView,
    "title" | "author" | "feeComment" | "resource"
  > {
  id: number;
  date: HostSystemItemView["outDateString"];
  fine: HostSystemItemView["fee"];
  resource: HostSystemItemView["resource"] | null;
}

export interface FinesSharedViewProps {
  page: number;
  pageSize: number;
  sort: HostSystemItemSortType;
  fees: HostSystemItemView[];
  feesTotalCount: number;
  feesTotalAmount: string;
  fetchPrintFines?: () => Promise<HostSystemItemView[]>;
  isStudent: boolean;
  routeIsLoading?: boolean;
  ecommerceEnabled: boolean;
}

export interface PrintFinesRow {
  id: number;
  title: string;
  feeComment: string;
  date: string;
  fine: number;
}

export const FinesSharedView: React.FC<FinesSharedViewProps> = ({
  page,
  pageSize,
  sort,
  fees,
  feesTotalCount,
  feesTotalAmount,
  fetchPrintFines,
  isStudent,
  routeIsLoading,
  ecommerceEnabled,
}) => {
  const { locationConfiguration } = useLocationConfiguration();
  const { scrollToTop } = useOutletContext<{ scrollToTop: () => void }>();
  const configName = locationConfiguration?.name;
  const revalidator = useRevalidator();
  const [searchParams, setSearchParams] = useSearchParams();
  const { translate } = useLanguage();
  const isMobile = useIsMobile();
  const theme = useTheme();
  const { handleRequestError } = useErrorDialog();
  const { showSimpleErrorDialog } = useSimpleErrorDialog();
  const { refreshAccountData } = useAccount();

  // ecommerce fee is a string, ensure it is a valid dollar amount
  function parseDollarAmount(value: string): number | null {
    const trimmed = value.trim();

    if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return null;
    }

    const num = Number(trimmed);

    if (!Number.isFinite(num)) {
      return null;
    }
    return Math.round(num * 100) / 100;
  }

  const ecommerceFee = useMemo(() => {
    const fee = locationConfiguration?.pacSettings.ecommerceFee;
    if (!fee) return null;
    let parsedFee: number | null = parseDollarAmount(fee);
    if (parsedFee && parsedFee > 0) return String(parsedFee?.toFixed(2));
    else return null;
  }, [locationConfiguration?.pacSettings]);

  const [selectedIds, setSelectedIds] = useState<number[]>([]); // chargeNumber used for id
  const selectedLength: number = selectedIds.length;

  const [isPrintLoading, setIsPrintLoading] = React.useState(false);
  const [isGridLoading, setIsGridLoading] = React.useState(false);
  const [printFinesContent, setPrintFinesContent] = React.useState<string>("");
  const [finesPrintDialogIsOpen, setFinesPrintDialogIsOpen] =
    useState<boolean>(false);

  const paymentProvider = "Authorize.net"; // TODO: This will need to be updated to be the selected provider once other 3rd party payments are added
  const paymentDialog = useDialog();
  const receiptDialog = useDialog();
  const [isPaymentDialogLoading, setIsPaymentDialogLoading] =
    useState<boolean>(false);
  const [paymentUrl, setPaymentUrl] = useState<string>("");
  const [paymentToken, setPaymentToken] = useState<string>("");
  const tokenSubmittedRef = React.useRef<string>("");
  const [paymentReceiptData, setPaymentReceiptData] =
    useState<FinesPaymentResponse | null>(null);
  const [isIframeLoading, setIsIframeLoading] = useState<boolean>(false);
  const [hasIframeError, setHasIframeError] = useState<boolean>(false);

  const [hasPaymentFinishError, setHasPaymentFinishError] =
    useState<boolean>(false);

  const showSkeletons =
    routeIsLoading || isPrintLoading || isPaymentDialogLoading;

  const finesCount: number = useMemo(() => {
    return fees.length;
  }, [fees]);

  const rows: GridRowsProp<FinesGridRowModel> = useMemo(() => {
    const fineRows = fees.map((fee) => {
      // TODO we need to assign to a const inside the map for type checking
      // find a better way without the const assignment.
      const rowModel: FinesGridRowModel = {
        id: parseInt(fee.chargeNumber || "", 10),
        title: fee.title,
        author: fee.author,
        feeComment: fee.feeComment,
        date: fee.outDateString,
        fine: fee.fee ?? 0,
        resource: fee.resource ?? null,
      };
      return rowModel;
    });
    return fineRows;
  }, [fees]);

  const isAllIndeterminate = selectedLength > 0 && selectedLength < rows.length;

  const selectedFines = useMemo(() => {
    return fees?.filter((fee) =>
      selectedIds.includes(parseInt(fee.chargeNumber || "", 10)),
    );
  }, [fees, selectedIds]);

  const generatePrintFinesGridHtml = (
    rows: PrintFinesRow[],
    translate: (key: string) => string,
  ): string => {
    const getTitleRowString = (row: PrintFinesRow): string => {
      const titleString =
        row.title && row.feeComment
          ? `${row.title} <br /> ${row.feeComment}`
          : row.title ?? row.feeComment ?? "";
      return titleString;
    };

    const tableRows = rows
      .map(
        (row) => `
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #ddd; word-break: break-word; white-space: normal;">
          ${getTitleRowString(row)}
          </td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: center;">${
            row.date
          }</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right;">${convertPenniesToCurrency(
            row.fine,
          )}</td>
        </tr>
      `,
      )
      .join("");

    return `
        <div style="width: 100%;">
          <table style="width: 100%; table-layout: fixed; border-collapse: collapse; font-family: sans-serif;">
            <thead>
              <tr>
                <th style="width: 90%; padding: 10px; border-bottom: 2px solid #333; text-align: left;">${translate(
                  "reasonForFine",
                )}</th>
                <th style="width: 90px; padding: 10px; border-bottom: 2px solid #333; text-align: center;">${translate(
                  "date",
                )}</th>
                <th style="width: 70px; padding: 10px; border-bottom: 2px solid #333; text-align: right;">${translate(
                  "fine",
                )}</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </div>
      `;
  };

  const handlePrintClick = async () => {
    if (!fetchPrintFines || isStudent) return;
    setIsPrintLoading(true);
    const allFinesData = await fetchPrintFines();
    const printRows: PrintFinesRow[] = allFinesData.map((fee, index) => {
      const title = fee.resource?.shortTitle ?? fee.title ?? null;
      const author = fee.resource?.shortAuthor ?? fee.author ?? null;
      const feeComment = fee.feeComment ?? null;

      const titleStr =
        title && author ? `${title} by ${author}` : title ?? null;

      return {
        id: index + 1,
        title: titleStr,
        feeComment: feeComment,
        date: fee.outDateString,
        fine: fee.fee ?? 0,
      };
    });

    const content: string = generatePrintFinesGridHtml(printRows, translate);
    setIsPrintLoading(false);
    setPrintFinesContent(content);
    setFinesPrintDialogIsOpen(true);
  };

  const closePrintDialog = useCallback(() => {
    setFinesPrintDialogIsOpen(false);
  }, [setFinesPrintDialogIsOpen]);

  const handleSelectAllChange = () => {
    const allIds: number[] = rows.map((row) => row.id);
    if (selectedLength >= rows.length) setSelectedIds([]);
    else setSelectedIds(allIds);
  };

  const handleChangePage = async (event: unknown, newPage: number) => {
    setSelectedIds([]); // clear selections on page change
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
    scrollToTop();
  };

  const handleChangeRowsPerPage = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    searchParams.set("page", "0");
    searchParams.set("size", event.target.value);
    setSearchParams(searchParams);
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    const newSort = event.target.value as HostSystemItemSortType;
    searchParams.set("page", "0");
    searchParams.set("sort", newSort);
    setSearchParams(searchParams);
  };

  const handlePayFines = async () => {
    if (!selectedFines || selectedFines.length < 1) return;
    setIsPaymentDialogLoading(true);
    const fineArgs: string[] = selectedFines.map((fine) => {
      const feeInCurrency = convertPenniesToCurrency(fine.fee || 0);

      const safeTitle = encodeURIComponent(
        fine.resource?.shortTitle ||
          fine.title ||
          `${translate("library")} ${translate("fine")}`,
      );

      return `${fine.chargeNumber},${feeInCurrency
        .replace("$", "")
        .replace(",", "")},${fine.fee},${safeTitle}`;
    });

    const queryString = new URLSearchParams({
      fines: fineArgs.join("|"),
    }).toString();

    let startData;
    try {
      // 1. Register the transaction and get invoice number
      const startRes = await axios.get(`/ecommerce/start?${queryString}`);
      if (!startRes.data || !startRes.data.invoiceNumber) {
        throw new Error("Missing invoice data in server response.");
      }
      startData = startRes.data;
    } catch (e) {
      let hint = translate("finesTransactionError");

      if (axios.isAxiosError(e)) {
        if (e.response?.data === "Invalid amount format") {
          hint = translate("finesPaymentInvalidAmount");
        }
      }

      handleRequestError(e, {
        hint: hint,
        onConfirm: () => {},
        confirmButtonText: translate("close"),
      });
      setIsPaymentDialogLoading(false);
      setIsIframeLoading(false);
      return;
    }

    // 2. Fetch Authorize.Net payment token
    try {
      const tokenParams = new URLSearchParams({
        amount: Number(startData.amount).toFixed(2), // guard against rounding errors
        invoiceNumber: startData.invoiceNumber,
        fines: startData.fines,
        payButtonText: translate("payFinesButtonText"),
        cancelButtonText: translate("cancel"),
      });

      const tokenRes = await axios.post("/ecommerce/accept/token", tokenParams);
      const tokenData = tokenRes.data;

      if (tokenData.result === "ok" && tokenData.token) {
        setHasIframeError(false);
        setIsIframeLoading(true);
        setPaymentToken(tokenData.token);
        setPaymentUrl(
          tokenData.paymentUrl ||
            "https://accept.authorize.net/payment/payment",
        );
        paymentDialog.openDialog();
      } else {
        // throw an explicit error so it hits the catch block
        throw new Error(
          tokenData.message || "Failed to generate payment token",
        );
      }
    } catch (e) {
      console.error("Error initiating payment:", e);
      // eCommerce API Login ID
      // eCommerce Transaction Key
      handleRequestError(e, {
        hint: translate("finesPaymentTokenError"),
        onConfirm: () => {},
        confirmButtonText: translate("close"),
      });
    }
    setIsPaymentDialogLoading(false);
  };

  const handleOnCloseFinesPaymentDialog = useCallback(() => {
    paymentDialog.closeDialog();
    setPaymentToken(""); // clear token
    tokenSubmittedRef.current = ""; // reset tracker
    setIsIframeLoading(false);
    setHasIframeError(false);
    return;
  }, []);

  const handleOnCloseFinesPaymentReceiptDialog = useCallback(async () => {
    setPaymentReceiptData(null);
    receiptDialog.closeDialog();
    setSelectedIds([]);
    setIsGridLoading(true);
    await Promise.all([refreshPage(), refreshAccountData()]);
    setIsGridLoading(false);
  }, []);

  const refreshPage = async () => {
    revalidator.revalidate();
  };

  // Auto submit token form into the iframe once modal is open
  useEffect(() => {
    if (paymentDialog.open && paymentToken) {
      // Prevent double-submission in React Strict Mode
      if (tokenSubmittedRef.current === paymentToken) {
        return;
      }

      // Mark token as submitted
      tokenSubmittedRef.current = paymentToken;

      // Allow Dialog to mount elements into the DOM
      const timer = setTimeout(() => {
        const form = document.getElementById("tokenForm") as HTMLFormElement;

        if (form) {
          form.submit();
        } else {
          console.error("The token form was not found in the DOM."); // should not happen
          setIsIframeLoading(false);
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [paymentDialog.open, paymentToken]);

  // Error handling if Authorize.Net fails to send the resize message
  // This will happen if the wrong eCommerce Domain Name is entered in LS2Admin Ecommerce config
  // Because we do not get a response for failures within the iframe, set to fail after 8s
  useEffect(() => {
    let fallbackTimer: ReturnType<typeof setTimeout>;

    // Only start the timer if the modal is open and the iframe is loading
    if (paymentDialog.open && isIframeLoading) {
      fallbackTimer = setTimeout(() => {
        console.warn("Authorize.Net iframe load timed out.");

        // kill the loading spinner, set iframe error, close the modal, show error dialog
        setIsIframeLoading(false);
        setHasIframeError(true);
        paymentDialog.closeDialog();
        handleRequestError(new Error("Iframe communication timeout"), {
          // Invalid eCommerce Domain Name
          // Devs need to use a tunnel for local build
          // QA should disconnect from VPN or set chrome://flags/#local-network-access-check and edge://flags/#local-network-access-check to disabled
          hint: `${translate("finesPaymentConnectionError")}`,
          onConfirm: () => {},
          confirmButtonText: translate("close"),
        });
      }, 8000);
    }
    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, [
    paymentDialog.open,
    paymentDialog.closeDialog,
    isIframeLoading,
    handleRequestError,
    translate,
  ]);

  // Listen for the postMessage from Authorize.net's iframe communicator
  useEffect(() => {
    const handleAuthorizeMessage = async (e: MessageEvent) => {
      // ignore React DevTools and other extension noise
      if (!e.data || e.data.source === "react-devtools-bridge") return;

      let action, response;
      if (typeof e.data === "string") {
        if (!e.data.includes("action=")) return;

        const params = new URLSearchParams(e.data);
        action = params.get("action");

        try {
          response = params.get("response")
            ? JSON.parse(params.get("response") as string)
            : null;
        } catch (err) {
          console.error("Failed to parse AuthNet response:", err);
        }
      } else if (typeof e.data === "object") {
        if (!e.data.action) return;
        action = e.data.action;
        response = e.data.response;
      }

      // if we still don't have an action, bail out
      if (!action) return;

      // User Cancellation
      if (action === "cancel") {
        paymentDialog.closeDialog();
        setPaymentToken(""); // clear the token
        tokenSubmittedRef.current = ""; // reset the strict-mode ref
        setIsIframeLoading(false);
        return;
      }
      // Handle iframe resizing
      if (action === "resizeWindow") {
        setIsIframeLoading(false);
        const h =
          typeof e.data === "object"
            ? e.data.height
            : new URLSearchParams(e.data).get("height");

        const iframe = document.getElementById("hostedPaymentIFrame");
        if (iframe && h) iframe.style.height = `${h}px`;
        return;
      }

      if (action === "transactResponse" && response) {
        setIsGridLoading(true);
        paymentDialog.closeDialog();
        // Authorize.Net responseCodes
        // "1" = Approved
        // "2" = Declined
        // "3" = Error
        // "4" = Held for Review
        switch (response.responseCode) {
          case "1":
            receiptDialog.openDialog();
            try {
              // 3. Finish - mark fines as paid
              await axios.post(
                `/ecommerce/accept/finish/${configName}`,
                response,
              );
            } catch (error) {
              console.error(
                "Error posting transaction to finish endpoint:",
                error,
              );
              setHasPaymentFinishError(true);
            }
            setPaymentReceiptData({
              ...response,
              merchant: locationConfiguration?.libraryName ?? "",
            });

            break;
          case "2": // Declined
            showSimpleErrorDialog(translate("finesPaymentDeclined"));
            break;
          case "4": // Held for Review
            showSimpleErrorDialog(translate("finesPaymentHeld"));
            break;
          default: // Response code 3 Error and any other undefined error
            showSimpleErrorDialog(translate("genericErrorMessage"));
            break;
        }
        setIsGridLoading(false);
      }
    };

    window.addEventListener("message", handleAuthorizeMessage);
    return () => window.removeEventListener("message", handleAuthorizeMessage);
  }, [configName, showSimpleErrorDialog, translate]);

  const FinesSelectAllCheckbox = () => (
    <Checkbox
      color="primary"
      disabled={showSkeletons || rows.length < 1}
      indeterminate={isAllIndeterminate}
      checked={selectedLength === rows.length}
      onChange={handleSelectAllChange}
      slotProps={{
        input: {
          "aria-label": translate("selectAll"),
        },
      }}
    />
  );

  return (
    <>
      {routeIsLoading || isPaymentDialogLoading || isGridLoading ? (
        <FinesToolbarSkeleton
          hideActions={isStudent || fees.length === 0}
          ecommerceEnabled={ecommerceEnabled}
        />
      ) : (
        // Title, Total, and Pay Fines button
        <Box
          sx={{
            pt: 2,
            pl: 2,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            color={fees?.length > 0 ? theme.palette.error.dark : "inherit"}
            fontWeight={fees?.length > 0 ? 500 : "inherit"}
          >
            {translate("fines")}: {feesTotalAmount}
          </Typography>
          {!isStudent && ecommerceEnabled && (
            <Button
              variant="contained"
              onClick={handlePayFines}
              disabled={selectedFines.length < 1}
            >
              {translate("payFinesButtonText")}
            </Button>
          )}
        </Box>
      )}

      {!routeIsLoading &&
        !isPaymentDialogLoading &&
        !isGridLoading &&
        finesCount > 0 && (
          <Box>
            {/* eCommerce Fee */}
            {!isStudent && ecommerceEnabled && ecommerceFee && (
              <Box sx={{ width: "100%" }}>
                <Typography
                  align="right"
                  variant="body2"
                  gutterBottom
                  sx={{ mb: 0, pb: 0 }}
                >
                  {translate("finesConvenienceFee", {
                    CONVENIENCE_FEE: `$${ecommerceFee}`,
                  })}
                </Typography>
              </Box>
            )}

            <Toolbar
              disableGutters
              sx={{
                flexWrap: "wrap",
                flexDirection: "row",
                alignItems: "center",
                ml: 2,
                pt: 1,
                pl: 0.5,
                pr: 0,
                ...(selectedLength > 0 && {
                  bgcolor: (theme) =>
                    alpha(
                      theme.palette.primary.main,
                      theme.palette.action.activatedOpacity,
                    ),
                }),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  my: isMobile ? 2 : 0,
                }}
              >
                {!isStudent && selectedLength > 0 ? (
                  <Typography
                    sx={{
                      flex: "1 1 100%",
                      textWrap: "nowrap",
                      ml: 1,
                    }}
                    color="inherit"
                    variant="subtitle1"
                    component="div"
                  >
                    {selectedLength} {translate("selected")}
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flex: isMobile ? "0 1 auto" : "1 1 100%",
                      justifyContent: isMobile ? "flex-start" : "flex-end",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    {!isStudent && (
                      <Button
                        onClick={handlePrintClick}
                        startIcon={<PrintIcon aria-hidden="true" />}
                        disabled={
                          showSkeletons || !rows || rows.length < 1 || isStudent
                        }
                      >
                        {`${translate("print")} ${translate("fines")}`}
                      </Button>
                    )}
                    <FinesSortControl
                      value={sort}
                      onChange={handleSortChange}
                    />
                  </Box>
                )}
                {isMobile && !isStudent && (
                  <Box
                    sx={{ ml: "auto", display: "flex", alignItems: "center" }}
                  >
                    {FinesSelectAllCheckbox()}
                  </Box>
                )}
              </Box>
            </Toolbar>
          </Box>
        )}

      {!routeIsLoading &&
        !isPaymentDialogLoading &&
        !isGridLoading &&
        finesCount < 1 && (
          <Alert sx={{ mt: 2, ml: 2 }} severity="info">
            {isStudent
              ? translate("studentNoFinesMessage")
              : translate("noFinesMessage")}
          </Alert>
        )}
      {/* Grid loading state skeltons */}
      {routeIsLoading || isPaymentDialogLoading || isGridLoading ? (
        <FinesGridSkeleton pageSize={pageSize} />
      ) : (
        finesCount > 0 && (
          <>
            <Box sx={{ width: "100%", minWidth: 0 }}>
              {isMobile ? (
                <Box
                  sx={{
                    width: "100%",
                    pr: 0,
                    pl: 2,
                    minWidth: 0,
                  }}
                >
                  <FinesMobileCards
                    rows={rows}
                    selectedIds={selectedIds}
                    isStudent={isStudent}
                    setSelectedIds={setSelectedIds}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    display: "grid",
                    pl: 2,
                    minWidth: 0,
                  }}
                >
                  <FinesDesktopGrid
                    rows={rows}
                    pageSize={pageSize}
                    selectedIds={selectedIds}
                    isStudent={isStudent}
                    setSelectedIds={setSelectedIds}
                  />
                </Box>
              )}

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={feesTotalCount}
                rowsPerPage={pageSize}
                labelRowsPerPage={translate("size")}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                slotProps={{
                  select: {
                    inputProps: { "aria-label": translate("rowsPerPage") },
                    native: true,
                  },
                }}
              />
            </Box>

            <PrintDialog
              open={finesPrintDialogIsOpen}
              onClose={closePrintDialog}
              dialogTitle={translate("fines")}
              htmlContent={printFinesContent}
              disabled={false}
            />

            {/* Authorize.Net Payment Modal */}
            <FinesPaymentDialog
              dialogProps={{
                ...paymentDialog.dialogProps,
                onClose: handleOnCloseFinesPaymentDialog, // token/iframe cleanup when closing
              }}
              paymentProvider={paymentProvider}
              paymentUrl={paymentUrl}
              paymentToken={paymentToken}
              isIframeLoading={isIframeLoading}
              hasIframeError={hasIframeError}
            />

            {/* Confirmation / receipt on successful payment */}
            <FinesPaymentReceiptDialog
              dialogProps={{
                ...receiptDialog.dialogProps,
                onClose: handleOnCloseFinesPaymentReceiptDialog, // clear receipt data when closing
              }}
              fines={selectedFines}
              receipt={paymentReceiptData || null}
              hasPaymentFinishError={hasPaymentFinishError}
            />
          </>
        )
      )}
    </>
  );
};
